// spec: SPEC-125
/**
 * What the product portal's dashboard is allowed to say, derived from `GET /api/portal/overview`
 * and `GET /api/org/members` and from nothing else.
 *
 * ================================================================================
 * WHY THIS FILE IS SO CAREFUL ABOUT WHICH NUMBERS EXIST
 * ================================================================================
 *
 * plan/portal-design.md audited every metric a Tunet dashboard would want against how it is
 * actually stored, and for the *product* side the answer was almost entirely "the prior value
 * has already been destroyed." Three storage shapes recur and telling them apart is the whole
 * analysis:
 *
 *   - Append-only with a timestamp → a prior-period value is a date comparison away.
 *   - Upserted / overwritten       → the row survives, the history does not. `RoomActivity` and
 *                                    `AppUser.LastSeenAt` both look like logs and are not.
 *   - In-process memory            → answers "right now", from one process, and never again.
 *                                    `RoomPresenceRegistry` is this.
 *
 * Concretely, and every one of these was checked against the entity definitions rather than
 * recalled: **`Room` has no `CreatedAt`**, so "rooms created this month" cannot exist. **`Room`
 * has no owner column and `RoomGrant` does not exist**, so "who owns this room" cannot be
 * answered — only "which team is it in", which is a different fact and is labelled as one.
 * **`RoomActivity` is upserted**, one row per (Room, User), so its timestamp has no history.
 * **`Connection` carries no timestamp at all** and **`Node` has `UpdatedAt` but no
 * `CreatedAt`**.
 *
 * **The one exception, and it contradicts plan/portal-design.md's own summary:** `Export` is
 * append-only with a write-once `CreatedAt` and is org-scoped, so exports carry a genuine
 * 30-day-over-30-day delta on the *product* side. That document's table says exactly this and
 * its next section then concludes that every honest delta "belongs to the admin portal". The
 * table is right.
 *
 * **The rule this file follows and refuses to break: no number without CONTEXT** — a delta, a
 * denominator, a recency, or a breakdown. Where none of the four is available the answer is not
 * a tile with an empty slot, and it is certainly not an invented comparison. It is a row in a
 * table, which is why the room list is a table.
 */

import type { OrgMemberDto, PortalOverviewDto, PortalRoomDto } from '../../generated/contracts';
import { isWithinDays } from './format';

/**
 * How far back "recently active" reaches. A week, because a working week is the unit a person
 * thinks in — not because anything in the schema suggests it.
 */
export const ACTIVE_WINDOW_DAYS = 7;

export type Direction = 'up' | 'down' | 'flat';

export function directionOf(recent: number, prior: number): Direction {
  return recent > prior ? 'up' : recent < prior ? 'down' : 'flat';
}

/**
 * The latest of a room's two activity stamps that this caller is entitled to.
 *
 * An owner is sent `lastActivityAt` (the room's maximum across everybody) *and*
 * `yourLastActiveAt`; a member is sent only their own, because the server never computes the
 * other one into their response. So "the newest thing I know about this room" is a max over
 * whichever of the two arrived, and a member's answer is genuinely narrower rather than
 * cosmetically hidden.
 */
export function newestKnownActivity(room: PortalRoomDto): string | null {
  const stamps = [room.lastActivityAt ?? null, room.yourLastActiveAt ?? null].filter(
    (stamp): stamp is string => stamp !== null,
  );

  if (stamps.length === 0) {
    return null;
  }

  return stamps.reduce((newest, stamp) => (stamp > newest ? stamp : newest));
}

export interface RoomMetrics {
  rooms: number;
  /** People in a room across the whole org at this instant. Live, and never stored. */
  hereNow: number;
  /** How many rooms have anybody in them at this instant. The denominator half of "here now". */
  roomsOccupied: number;
  /** Distinct teams the rooms belong to — a breakdown, and the honest stand-in for ownership. */
  teams: number;
  /**
   * Rooms with activity inside {@link ACTIVE_WINDOW_DAYS}, counted from whichever stamp this
   * caller was entitled to. For an owner that is "rooms anyone worked in"; for a member it is
   * "rooms I worked in", and the label the view puts on it changes with the role for exactly
   * that reason.
   */
  roomsActiveRecently: number;
  /** Rooms this caller has ever been active in. A breakdown of the room list against itself. */
  roomsYouHaveUsed: number;
  /** The caller's own most recent activity anywhere, or null. */
  yourLastActiveAt: string | null;
  /** And which room it was in, so the recency is not a bare timestamp. */
  yourLastRoomName: string | null;
}

export function roomMetrics(overview: PortalOverviewDto, now: Date = new Date()): RoomMetrics {
  const rooms = overview.rooms;

  let yourLastActiveAt: string | null = null;
  let yourLastRoomName: string | null = null;

  for (const room of rooms) {
    const yours = room.yourLastActiveAt ?? null;

    if (yours !== null && (yourLastActiveAt === null || yours > yourLastActiveAt)) {
      yourLastActiveAt = yours;
      yourLastRoomName = room.name;
    }
  }

  return {
    rooms: rooms.length,
    hereNow: rooms.reduce((sum, room) => sum + room.hereNow, 0),
    roomsOccupied: rooms.filter((room) => room.hereNow > 0).length,
    teams: new Set(rooms.map((room) => room.teamName).filter((name) => name.length > 0)).size,
    roomsActiveRecently: rooms.filter((room) =>
      isWithinDays(newestKnownActivity(room), ACTIVE_WINDOW_DAYS, now),
    ).length,
    roomsYouHaveUsed: rooms.filter((room) => (room.yourLastActiveAt ?? null) !== null).length,
    yourLastActiveAt,
    yourLastRoomName,
  };
}

export interface PeopleMetrics {
  people: number;
  /**
   * People stamped within {@link ACTIVE_WINDOW_DAYS}. A **recency**, not a delta:
   * `AppUser.LastSeenAt` is a single overwritten column, debounced to at most one stamp per
   * connection per minute, and null with no backfill for anyone inactive since it shipped —
   * "active this week versus last week" cannot exist and must not be implied.
   */
  activeRecently: number;
  /** People who have never been stamped at all. "Never seen" is not "seen a long time ago". */
  neverSeen: number;
  owners: number;
}

/** Owner-only, because the member list it reads is owner-only on the server. */
export function peopleMetrics(members: readonly OrgMemberDto[], now: Date = new Date()): PeopleMetrics {
  return {
    people: members.length,
    activeRecently: members.filter((m) => isWithinDays(m.lastSeenAt ?? null, ACTIVE_WINDOW_DAYS, now)).length,
    neverSeen: members.filter((m) => (m.lastSeenAt ?? null) === null).length,
    owners: members.filter((m) => m.role === 'owner').length,
  };
}

/**
 * Room rows ordered for scanning rather than alphabetically: occupied rooms first, then the
 * most recently active, then by name.
 *
 * The server already returns them case-insensitively by name — which is the right answer for the
 * headset's room picker (`GET /api/rooms`) and the wrong one here. A dashboard's first question
 * is "is anything happening", and the answer to that must not depend on what the rooms were
 * called.
 */
export function forScanning(rooms: readonly PortalRoomDto[]): PortalRoomDto[] {
  return [...rooms].sort((a, b) => {
    if (a.hereNow !== b.hereNow) {
      return b.hereNow - a.hereNow;
    }

    const left = newestKnownActivity(a);
    const right = newestKnownActivity(b);

    if (left !== right) {
      // A room nobody has ever touched sorts last in both cases rather than being treated as
      // the beginning of time — the same distinction the admin roster draws between a null
      // stamp and an old one.
      if (left === null) {
        return 1;
      }

      if (right === null) {
        return -1;
      }

      return right.localeCompare(left);
    }

    return a.name.localeCompare(b.name);
  });
}
